import { type FC } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify/unstyled';

import { useAppDispatch } from '@app/store/store';
import { useModal } from '@contexts/modal-context/ModalContext';
import { setCredentials } from '@features/authentication/store/authentication.slice';
import { notify, resetAllApiState } from '@shared/utils';

import { type LoginArgs, useLoginMutation } from '../api/authentication.api';
import { LoginForm } from '../components/LoginForm';

type UserAgreementData = {
  tenantId: string;
  token: string;
  email: string;
};

export const Login: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [login, { isLoading }] = useLoginMutation();

  const handleTermsRequired = (data: UserAgreementData) => {
    openModal('USER_AGREEMENT', data);
  };

  const onFinish = async ({ email, password }: LoginArgs): Promise<void> => {
    try {
      const data = await login({ email, password }).unwrap();
      const { message, token, tenants } = data;

      /* Single tenant success: If authentication is successful here, it means the user has only one tenant and will be 
      redirected to the main page as normal */
      if (message === 'Authentication successful') {
        dispatch(resetAllApiState());
        dispatch(setCredentials({ username: email, token }));
        notify({
          type: 'success',
          title: 'Login successful',
          description: message,
        });
        void navigate('/');
        return;
      }

      /* Multi-tenant scenario: If you see the select tenant to login message it means the user has multiple tenants.
      This will trigger the ui to show the select tenant modal. The user will log in and be redirected to the main page like 
      normal after selecting a tenant */
      if (message === 'Select a tenant to login') {
        if (!tenants) {
          toast.error('No tenants available for selection.');
          return;
        }
        openModal('SELECT_TENANT', {
          tenants: tenants,
          username: email,
          password: password,
          onTermsRequired: handleTermsRequired,
        });
        return;
      }

      /* User agreement not accepted yet: If the user has not accepted the terms and conditions, UI will display the user agreement modal.*/
      if (message === 'Tenant has not accepted the terms and conditions') {
        const tenantId = tenants?.[0]?.tenantId ?? '';
        openModal('USER_AGREEMENT', {
          tenantId: tenantId,
          token: token,
          email: email,
        });
        return;
      }

      // Fallback for unexpected messages
      notify({
        type: 'error',
        title: 'Unexpected login response',
        description: message,
      });
    } catch (error) {
      console.error('Authentication error', error);
      notify({
        type: 'error',
        title: 'Authentication error',
        description: 'Unable to log in. Please try again.',
      });
    }
  };

  return <LoginForm onFinish={onFinish} isLoading={isLoading} />;
};
